import { put, takeLatest, select, call } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { push } from 'connected-react-router';

import api, { authApi } from 'api';
import { actions as appActions } from 'state/app';
import { actions as uiActions } from 'state/ui';
import { handleInitApp as initApp } from 'state/app/effects';

import {
  actions as analyticsActions,
  constants as analyticsEvents,
} from 'state/analytics';
import { actions as errorActions } from 'state/errors';

import routes from 'utils/routes';

import selectors from 'state/auth/selectors';
import types from 'state/auth/actionTypes';
import authActions from 'state/auth/actionCreators';
import keyGenerator from 'utils/keys';
import { isEmpty } from 'utils/types';
import { destroyBeamer } from 'analytics/beamer';

function* handleSendUserInvitation({ payload }) {
  const { invitationUserData } = payload;
  const user = yield select(selectors.getUser);

  try {
    yield call(api.sendUserInvitation, {
      shipperId: user.shipper_id,
      invitationUserData,
    });
    yield put(
      uiActions.setFeedback({
        feedback: 'InviteUserModal:send_action.success',
        params: {
          email: invitationUserData.username,
        },
      })
    );
    yield call(delay, 3300);
  } catch (error) {
    yield put(
      uiActions.setFeedback({
        feedback: 'InviteUserModal:send_action.error',
        success: false,
      })
    );
    yield call(delay, 50000);
  } finally {
    yield put(uiActions.setFeedback({ feedback: null }));
  }
}

function* handleUpdateUser({ payload }) {
  const { userData } = payload;

  yield put(errorActions.cleanErrors());

  const errors = ['first_name', 'last_name'].reduce(
    (errorAcc, value) => ({
      ...errorAcc,
      ...(isEmpty(userData[value])
        ? { [keyGenerator.settings[value]]: 'fieldRequired' }
        : {}),
    }),
    {}
  );

  if (!isEmpty(errors)) {
    yield put(errorActions.setError(errors));
    return;
  }

  const user = yield select(selectors.getUser);
  try {
    yield call(api.updateUser, { userId: user.id, userData });
    yield put(uiActions.setFeedback({ feedback: 'Settings:update.success' }));
    yield call(delay, 3300);
  } catch (error) {
    yield put(
      uiActions.setFeedback({
        feedback: 'Settings:update.error',
        success: false,
      })
    );
    yield call(delay, 50000);
  } finally {
    yield put(uiActions.setFeedback({ feedback: null }));
  }
}

function* handleLogin({ payload }) {
  const { username, password } = payload;

  yield put(authActions.setLoading(true));

  try {
    const { data } = yield call(authApi.getToken, { username, password });
    yield put(authActions.setCredentials(data));
    yield call(initApp);
    yield put(
      analyticsActions.sendEvent(
        analyticsEvents.auth.login({
          success: true,
          email: username,
          wrong_credentials: false,
        })
      )
    );
  } catch (error) {
    yield put(authActions.setLoading(false));

    const credentialsProblem = error.response && error.response.status === 401;

    if (credentialsProblem) {
      yield put(authActions.setError('authLoginForm:errors:wrong_credentials'));
    } else {
      yield put(authActions.setError('authLoginForm:errors:generic'));
    }

    yield put(
      analyticsActions.sendEvent(
        analyticsEvents.auth.login({
          success: false,
          email: username,
          wrong_credentials: credentialsProblem,
        })
      )
    );
  }
}

function* handleSignUp({ payload }) {
  const { name, surname, password, userDataHash } = payload;
  let shipperData = {};
  try {
    shipperData = JSON.parse(atob(decodeURIComponent(userDataHash)));
  } catch (e) {
    yield put(authActions.setError('authSignUpForm:error.generic'));
  }
  const { email, country, company_name: companyName } = shipperData;
  const upperCaseCountry = country.toUpperCase();
  if (
    ![email, upperCaseCountry, companyName, name, surname, password].find(
      (empty) => empty
    )
  ) {
    yield put(authActions.setError('authSignUpForm:error.generic'));
    return;
  }
  const DEFAULT_LANGUAGE_FOR_NEW_SHIPPERS = 'en-GB';
  let language = DEFAULT_LANGUAGE_FOR_NEW_SHIPPERS;
  if (upperCaseCountry === 'ES') {
    language = 'es-ES';
  } else if (upperCaseCountry === 'FR') {
    language = 'fr-FR';
  }
  try {
    yield put(authActions.setLoading(true));
    yield call(api.post, 'create_shipper/', {
      first_name: name,
      last_name: surname,
      password,
      ...shipperData,
      country: upperCaseCountry,
      language,
    });
    yield put(
      analyticsActions.sendEvent(
        analyticsEvents.auth.signupLoginSuccessful({
          companyName,
          email,
          country: upperCaseCountry,
        })
      )
    );
    yield call(handleLogin, {
      payload: {
        username: email,
        password,
      },
    });
  } catch (e) {
    const data = e.response?.data;
    yield console.log(e);
    if (data?.email) {
      yield put(authActions.setError('authSignUpForm:error.already_register'));
    } else {
      yield put(authActions.setError('authSignUpForm:error.generic'));
    }

    yield put(
      analyticsActions.sendEvent(
        analyticsEvents.auth.signupLoginFailed({
          companyName,
          email,
          country: upperCaseCountry,
        })
      )
    );
  } finally {
    yield put(authActions.setLoading(false));
  }
}

function* handleLogout() {
  try {
    const token = yield select(selectors.getAccessToken);
    yield call(api.revokeToken, token);
    yield call(api.cleanAuthHeader);
  } catch (e) {
    yield console.log(e);
  } finally {
    yield put(analyticsActions.sendEvent(analyticsEvents.auth.logout()));
    yield put(appActions.resetApp());
    yield put(push(routes.login));
    destroyBeamer();
  }
}

export default function* () {
  yield takeLatest(types.sendUserInvitation$, handleSendUserInvitation);
  yield takeLatest(types.updateUser$, handleUpdateUser);
  yield takeLatest(types.login$, handleLogin);
  yield takeLatest(types.signUp$, handleSignUp);
  yield takeLatest(types.logout$, handleLogout);
}

export {
  handleSendUserInvitation,
  handleUpdateUser,
  handleLogin,
  handleSignUp,
  handleLogout,
};
